/**
 * Gestão Centralizada de Produtos (contexto Office.adv) — CATÁLOGO MOCK.
 *
 * Este arquivo é a FONTE DE METADADOS (o que renderizar) desta feature, na fase frontend-first.
 * Separa metadata (catálogo, aqui) de valores (o que o escritório configurou — ver estado do módulo).
 *
 * ⚠️ MOCK / PROVISÓRIO:
 *  - `produtoId` deve ser confirmado contra a tabela `produto` real; enquanto isso usamos `slug`
 *    como identificador estável no frontend (`produtoId: null` = a confirmar).
 *  - Chaves `info` marcadas como (grounded) existem no código; as demais são plausíveis e devem ser
 *    confirmadas contra o app do tenant Office / o cadastro de produtos.
 *  - Casos complexos (ex.: Publicações via Importador de notas) entram SIMPLIFICADOS (`simplificado`),
 *    cobrindo apenas gestão básica de token da coligada.
 *
 * Ref.: openspec/changes/archive/2026-07-06-gestao-centralizada-produtos/design.md (Decisões 2–6)
 *       e openspec/specs/gestao-produtos/spec.md (comportamento fonte-da-verdade).
 */

// ── Schema do metadata (Task 1.1) ────────────────────────────────────────────
/**
 * @typedef {'text'|'password'|'select'|'url'|'toggle'} TipoCampo
 *
 * @typedef {Object} CampoIntegracao
 * @property {string}   chave              Identidade estável do campo; mapeia p/ a chave `public.info`
 *                                         (ex.: 'intProtocol.partner.token'). Usada no payload/write-through.
 * @property {string}   label              Rótulo exibido.
 * @property {TipoCampo} tipo              Tipo do input.
 * @property {boolean}  [obrigatorioSeAtivo] Só é obrigatório quando o produto (ou grupo) está ativo.
 * @property {string[]} [opcoes]           Opções (quando tipo === 'select').
 * @property {string}   [placeholder]
 * @property {string}   [ajuda]            Texto de ajuda opcional.
 *
 * @typedef {Object} ConfigGrupo           Configuração compartilhada por uma categoria ("token do grupo").
 * @property {string}          label
 * @property {string}          [descricao] Texto exibido acima dos campos (AcordeaoCategoriaProduto.vue).
 * @property {CampoIntegracao[]} campos    Observações é sempre exibida após estes campos, como último.
 *
 * @typedef {Object} ProdutoMeta
 * @property {number|null} produtoId       FK p/ `produto` (null = a confirmar).
 * @property {string}      slug            Identificador estável no frontend.
 * @property {string}      nome
 * @property {string|null} coligada        Provedor coligada prioritário (ex.: 'Leijud', 'Cria.AI', 'Preâmbulo Bank').
 * @property {boolean}     [fornecimentoExterno] Se admite provedor externo além da coligada.
 * @property {boolean}     [usaTokenGrupo] Delega o formulário inteiro (campos + Observações) à config
 *                                         de grupo da categoria; não exibe token nem Observações
 *                                         próprios. Hoje só 'resumoIa' usa isso — é exclusivo dele.
 * @property {boolean}     [simplificado]  Caso complexo coberto de forma simplificada (só token).
 * @property {CampoIntegracao[]} campos    Campos próprios (vazio quando usaTokenGrupo). Observações é
 *                                         sempre exibida (LinhaProduto.vue), exceto quando usaTokenGrupo.
 *
 * @typedef {Object} CategoriaProduto
 * @property {string}         id
 * @property {string}         nome
 * @property {ConfigGrupo|null} [grupo]    Config de grupo da categoria (ex.: IA), quando houver.
 * @property {string}         [placeholder] Texto exibido quando a categoria não tem produtos ("Em breve").
 * @property {ProdutoMeta[]}  produtos
 */

// ── Mock do catálogo (Task 1.2 + 1.3) ────────────────────────────────────────
/** @type {CategoriaProduto[]} */
export const CATALOGO_PRODUTOS = [
  {
    id: 'api',
    nome: 'API',
    grupo: null,
    placeholder: 'Em breve',
    produtos: []
  },
  {
    id: 'capturaProcessual',
    nome: 'Captura processual',
    grupo: null,
    produtos: [
      {
        produtoId: 10,
        slug: 'andamentos',
        nome: 'Andamentos',
        coligada: null,
        fornecimentoExterno: true,
        campos: [
          { chave: 'intAndamentos.partner.token', label: 'Token', tipo: 'password', obrigatorioSeAtivo: true }
        ]
      },
      {
        produtoId: null,
        slug: 'distribuicoes',
        nome: 'Distribuições',
        coligada: null,
        campos: [
          { chave: 'intDistribuicoes.partner.token', label: 'Token', tipo: 'password', obrigatorioSeAtivo: true }
        ]
      },
      {
        produtoId: null,
        slug: 'intimacoes',
        nome: 'Intimações',
        coligada: 'Leijud',
        simplificado: true,
        campos: [
          { chave: 'intIntimacoes.partner.token', label: 'Token', tipo: 'password', obrigatorioSeAtivo: true }
        ]
      },
      {
        produtoId: null,
        slug: 'preCadastro',
        nome: 'Pré-cadastro',
        coligada: null,
        campos: [
          // (a confirmar) chave placeholder
          { chave: 'intPreCadastro.partner.token', label: 'Token', tipo: 'password', obrigatorioSeAtivo: true }
        ]
      },
      // Caso complexo (Importador de notas / projeto Importer): versão SIMPLIFICADA = só token da coligada.
      {
        produtoId: 8,
        slug: 'publicacoes',
        nome: 'Publicações',
        coligada: 'Leijud',
        simplificado: true,
        campos: [
          { chave: 'intPublicacoes.partner.token', label: 'Token', tipo: 'password', obrigatorioSeAtivo: true }
        ]
      }
    ]
  },
  {
    id: 'financeiro',
    nome: 'Finanças',
    grupo: null,
    produtos: [
      {
        produtoId: null,
        slug: 'bank',
        nome: 'Bank',
        coligada: 'Preâmbulo Bank',
        campos: [
          { chave: 'intBank.partner.token', label: 'Token', tipo: 'password', obrigatorioSeAtivo: true },
          { chave: 'intBank.partner.url', label: 'URL', tipo: 'url' }
        ]
      },
      {
        produtoId: 2,
        slug: 'financeiro',
        nome: 'Financeiro',
        coligada: null,
        campos: []
      }
    ]
  },
  {
    id: 'ia',
    nome: 'Inteligência Artificial',
    grupo: {
      label: 'Provedor de IA',
      descricao: 'Configuração do Resumo IA.',
      campos: [
        // (grounded) intAiPrompt.partner.* é lido pelo backend hoje.
        {
          chave: 'intAiPrompt.partner.id',
          label: 'Provedor',
          tipo: 'select',
          obrigatorioSeAtivo: true,
          opcoes: ['criaAi', 'gemini', 'gpt', 'claude', 'mistral', 'deepseek']
        },
        { chave: 'intAiPrompt.partner.token', label: 'Token', tipo: 'password', obrigatorioSeAtivo: true },
        { chave: 'intAiPrompt.partner.url', label: 'URL', tipo: 'url' },
        { chave: 'intAiPrompt.partner.model', label: 'Modelo', tipo: 'text' }
      ]
    },
    produtos: [
      { produtoId: null, slug: 'jarvis', nome: 'Cálculo', coligada: null, campos: [] },
      { produtoId: null, slug: 'criaAi', nome: 'Documento', coligada: null, campos: [] },
      { produtoId: null, slug: 'preditivo', nome: 'Preditivo', coligada: null, campos: [] },
      { produtoId: null, slug: 'resumoIa', nome: 'Resumo', coligada: null, usaTokenGrupo: true, campos: [] }
    ]
  },
  {
    id: 'operacaoJuridica',
    nome: 'Operação Jurídica',
    grupo: null,
    produtos: [
      {
        produtoId: null,
        slug: 'gerarPrazo',
        nome: 'Gerar prazo automático',
        coligada: null,
        campos: []
      },
      {
        produtoId: null,
        slug: 'protocolo',
        nome: 'Protocolo',
        coligada: null,
        fornecimentoExterno: true,
        campos: [
          // (grounded no doc) intProtocol.partner.*
          { chave: 'intProtocol.partner.tokenPeticao', label: 'Token Petição', tipo: 'password', obrigatorioSeAtivo: true },
          { chave: 'intProtocol.partner.token', label: 'Token', tipo: 'password', obrigatorioSeAtivo: true },
          { chave: 'intProtocol.partner.urlUpdate', label: 'URL Update', tipo: 'url' }
        ]
      },
      // Só Observações (sem token): produto sem campos próprios. O produtoId 108 é o produto de
      // integração do Office (produto.id), criado pela migração issue-23237.
      {
        produtoId: 108,
        slug: 'sincronizarAndamentos',
        nome: 'Sincronizar andamentos',
        coligada: null,
        campos: []
      }
    ]
  }
]

// ── Mock de estado inicial (Task 1.2 — "mock de estado inicial") ──────────────
/**
 * Exemplo de `valores` alinhado ao protótipo (Cria.AI ativo com observações; demais inativos).
 * Estrutura de persistência-alvo: por escritório e por produto → { ativo, valor(JSON) }.
 * @type {Record<string, { ativo: boolean, valor: Object }>}
 */
export const VALORES_INICIAIS_MOCK = {
  // config de grupo (categoria IA) — persistida como uma entrada própria (chave = prefixo do grupo)
  intAiPrompt: { ativo: true, valor: { 'intAiPrompt.partner.id': 'criaAi', 'intAiPrompt.partner.token': '', 'intAiPrompt.partner.url': '', 'intAiPrompt.partner.model': '' } },
  criaAi: { ativo: true, valor: { observacoes: '' } },
  resumoIa: { ativo: false, valor: {} },
  preditivo: { ativo: false, valor: {} },
  jarvis: { ativo: false, valor: {} }
}

/**
 * Constrói o estado `valores` padrão (tudo inativo, campos vazios) a partir do catálogo.
 * Pura — sem dependência de store; útil para inicializar o formulário quando não há config salva.
 * @param {CategoriaProduto[]} [catalogo]
 * @returns {Record<string, { ativo: boolean, valor: Object }>}
 */
export function montarValoresPadrao (catalogo = CATALOGO_PRODUTOS) {
  const valores = {}
  catalogo.forEach((categoria) => {
    if (categoria.grupo) {
      const grupoChave = chaveDoGrupo(categoria)
      valores[grupoChave] = { ativo: false, valor: valoresVaziosDosCampos(categoria.grupo.campos) }
    }
    categoria.produtos.forEach((produto) => {
      valores[produto.slug] = { ativo: false, valor: valoresVaziosDosCampos(produto.campos, !produto.usaTokenGrupo) }
    })
  })
  return valores
}

/** Deriva a chave de persistência da config de grupo (prefixo comum dos campos do grupo). */
export function chaveDoGrupo (categoria) {
  const primeira = categoria.grupo && categoria.grupo.campos[0] && categoria.grupo.campos[0].chave
  // ex.: 'intAiPrompt.partner.id' -> 'intAiPrompt'
  return primeira ? primeira.split('.')[0] : `grupo_${categoria.id}`
}

function valoresVaziosDosCampos (campos, incluirObservacoes = true) {
  const valor = incluirObservacoes ? { observacoes: '' } : {}
  campos.forEach((campo) => { valor[campo.chave] = campo.tipo === 'toggle' ? false : '' })
  return valor
}

export default CATALOGO_PRODUTOS
